// /src/components/core/HeadersAndFooters/HeaderButtonsFeed.jsx
// Header buttons overlay for feed pages with context switching dropdown
// RESPONSIVE: Renders compact 50px mobile layout or full 80px desktop layout
//
// LANGUAGE/COUNTRY SELECTOR: Language selector (🇵🇹 flag + EN/PT dropdown)
// positioned left of Profile button. Connected to database-driven translation system.
// Stored in localStorage key 'ournet_language' + TranslationProvider context.

import React, { useState, useEffect, useRef } from 'react';
import { ChevronDown, Book, Newspaper, MessageCircle, Calendar, Users } from 'lucide-react';
import { useTheme } from '../../../themes/ThemeProvider';
import { useAuth, isSignedIn } from '../../../contexts/AuthContext';
import { useMediaQuery, clampScale } from '../../../hooks/useMediaQuery';
import { useTranslation, useCountryInView } from '../../../i18n/useTranslation';
import StandardButton from '../StandardButton';
import { tokens } from '../../../styles/designTokens';
import ProfileMenu from './ProfileMenu';
import LocalitySelector from '../../locality/LocalitySelector';
import MapLocationPicker from '../../locality/MapLocationPicker';
import LanguageSelector from '../LanguageSelector';
import IssuesDropdown from '../IssuesDropdown';
import NotificationsDropdown from '../NotificationsDropdown';
import NotificationsBell from '../NotificationsBell';
import AnimatedSearchBar from '../AnimatedSearchBar';
// Shared mobile front-page action line (create pill + inline search) — used by both the locality
// and group front-page headers (Mobile Groups, Commit 6).
// Shared mobile-header left cluster (logo + pill) — keeps the feed and detail headers dimensionally
// identical so the left pill/logo don't jump on feed↔detail (Mobile Groups, Commit 7).
import MobileHeaderLeftCluster from './MobileHeaderLeftCluster';
// InstallAppPrompt removed from the header (Mobile Groups, Commit 5) — install now lives in the
// profile menu (ProfileMenu, context-agnostic). Still used by the Directory tab body elsewhere.
import { isPlatformAdmin } from '../../../utils/adminHelpers';
import { supabaseFetch } from '../../../lib/supabase';
import { updateAppBadge } from '../../../lib/pushNotifications';
import { getCountryFlag } from '../../../utils/countryHelpers';
import { resolveDefaultCountry, getBoundaryPlace } from '../../../data/countriesService';
import { getCachedIpLocation } from '../../../utils/ipGeolocation';
import { baseParishId } from '../../../utils/scopeResolution';

function HeaderButtonsFeed({ currentContext, currentMode, selectedGroup, selectedArea, setSelectedArea, onContextSwitch, onGuestLocationChange, frontPageActive = false, mobileScreen = 'news', setMobileScreen, visitArea = null, onEnterVisit, onClearVisit, hideSearch = false }) {
  // Note: isLoggedIn and onShowLoginModal props removed - now using useAuth() context directly
  // selectedArea and setSelectedArea now come from App.js to persist across navigation
  const { requireAuth, currentUser: authUser, goToLogin } = useAuth();
  const { isMobile, sidebarWidth, windowWidth } = useMediaQuery();

  // Shared desktop title scales 16px@1200 -> 26px@1800+ (LAYOUT.md § Page header title, brief
  // 2026-09-05). Same clampScale shape as the dynamic sidebar width; flat below 1200 (mobile
  // interface takes over there) and flat at/above 1800.
  const desktopTitleFontSize = clampScale(windowWidth, { start: 1200, end: 1800, minVal: 16, maxVal: 26 });
  const { t } = useTranslation('ui');
  const { t: tFp } = useTranslation('frontpage');
  const { t: tLoc } = useTranslation('location');
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [showLocalityButton, setShowLocalityButton] = useState(false);
  const [showLocalitySelector, setShowLocalitySelector] = useState(false);
  const [userAreas, setUserAreas] = useState([]);
  const [areaLocalities, setAreaLocalities] = useState([]);
  const [homeLocalityName, setHomeLocalityName] = useState('');
  // The country in view (Roadmap #85, A3): shared state derived once in App.js, no longer this
  // header's own — the flag here and the language rule read the same value.
  const countryInView = useCountryInView();
  const [loading, setLoading] = useState(true);
  const [issueCount, setIssueCount] = useState(0);
  const [showIssuesDropdown, setShowIssuesDropdown] = useState(false);
  // In-app notifications inbox (Notifications Commit 1) — bell + unread count.
  // Logged-in users only; mirrors the admin issue-count triad above.
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showQuickPicker, setShowQuickPicker] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [hasAdminAlerts, setHasAdminAlerts] = useState(false);
  const theme = useTheme();

  // Track if initial area selection has been done (prevents resetting on viewport change)
  const hasInitializedArea = useRef(false);
  // Track the area ID currently being loaded to prevent duplicate/stale loads
  const loadingAreaIdRef = useRef(null);
  // Inline mobile search state/refs/effect relocated into MobileActionLine (Mobile Groups, Commit 6).

  // Load user's areas and localities on mount and when user/selectedArea changes
  useEffect(() => {
    console.log('🔍 HeaderButtonsFeed useEffect triggered - selectedArea:', selectedArea, 'authUser:', authUser?.id);
    // Non-blocking load - errors won't break the component
    // Pass selectedArea explicitly to ensure we have the current prop value
    loadUserAreas(null, selectedArea).catch(err => {
      console.error('Failed to load user areas:', err);
      setLoading(false);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authUser?.id, selectedArea?.id]);

  // Listen for location refresh events (fired on login/logout)
  useEffect(() => {
    const handleRefreshLocations = (event) => {
      // Event may include userId directly (for cases where React state hasn't propagated yet)
      const eventUserId = event?.detail?.userId;
      console.log('HeaderButtonsFeed: Refresh event received, userId from event:', eventUserId);

      // Reset initialization flag to allow reloading for new user
      hasInitializedArea.current = false;

      // Pass null for selectedArea on refresh (we want to reload from scratch on login/logout)
      loadUserAreas(eventUserId, null).catch(err => {
        console.error('Failed to reload user areas:', err);
      });
    };

    window.addEventListener('ournet:refresh-locations', handleRefreshLocations);
    return () => {
      window.removeEventListener('ournet:refresh-locations', handleRefreshLocations);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Check if admin user (explicit users.is_sys_admin grant) and load issue count
  useEffect(() => {
    // Use authUser from context (set by App.js from localStorage)
    if (authUser) {
      setCurrentUser(authUser);
      // Admin access via an explicit is_sys_admin grant, not an email-domain pattern
      const userIsAdmin = isPlatformAdmin(authUser);
      setIsAdmin(userIsAdmin);
      console.log('HeaderButtonsFeed: User is admin:', userIsAdmin, authUser.email);

      if (userIsAdmin) {
        loadIssueCount();
      }
    } else {
      // An in-session logout sets authUser to null (App.js handleLogout does not reload), so
      // without this an admin's Shield, issue count and alert dot survived logout. The guest
      // object already resets via isPlatformAdmin above; this covers the null path.
      setCurrentUser(null);
      setIsAdmin(false);
      setIssueCount(0);
      setHasAdminAlerts(false);
      setShowIssuesDropdown(false);
    }
  }, [authUser]);

  // Listen for issues updated events
  useEffect(() => {
    const handleIssuesUpdated = () => {
      if (isAdmin) {
        loadIssueCount();
      }
    };

    window.addEventListener('ournet:issues-updated', handleIssuesUpdated);
    return () => {
      window.removeEventListener('ournet:issues-updated', handleIssuesUpdated);
    };
  }, [isAdmin]);

  // Listen for show issues dropdown event from ProfileMenu
  useEffect(() => {
    const handleShowIssuesDropdown = () => {
      if (isAdmin) {
        loadIssueCount();
        setShowIssuesDropdown(true);
      }
    };

    window.addEventListener('ournet:showIssuesDropdown', handleShowIssuesDropdown);
    return () => {
      window.removeEventListener('ournet:showIssuesDropdown', handleShowIssuesDropdown);
    };
  }, [isAdmin]);

  const loadIssueCount = async () => {
    try {
      // Use supabaseFetch to avoid hanging client
      // Counts reports the human hasn't acknowledged — its own marker, independent of the
      // Deck-sync/digest jobs' own delivery markers and the report's status lifecycle.
      const { data, error } = await supabaseFetch.query('error_reports', {
        select: 'id',
        is: { acknowledged_at: null },
        limit: 100
      });

      if (error) {
        console.error('Error loading issue count:', error);
        return;
      }

      setIssueCount(data?.length || 0);

      // Check if there are any admin alerts (error_reports OR active maintenance items)
      const hasAlerts = (data?.length || 0) > 0;
      setHasAdminAlerts(hasAlerts);
    } catch (err) {
      console.error('Error loading issue count:', err);
    }
  };

  // --- Notifications inbox (Commit 1) — unread count loader + refresh wiring ---
  // Mirrors the admin issue-count triad: supabaseFetch count → ournet:* event refresh → red badge.
  const loadUnread = async () => {
    if (!authUser?.id) return;
    try {
      const { data, error } = await supabaseFetch.query('notifications', {
        select: 'id',
        eq: { recipient_id: authUser.id, is_read: false },
        limit: 100
      });
      if (error) {
        console.error('Error loading unread notifications:', error);
        return;
      }
      setUnreadCount(data?.length || 0);
    } catch (err) {
      console.error('Error loading unread notifications:', err);
    }
  };

  // Load unread on mount + when the logged-in user changes. Guests have no inbox.
  useEffect(() => {
    if (authUser?.id) {
      loadUnread();
    } else {
      setUnreadCount(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authUser?.id]);

  // Refresh on the notifications-updated event (fired after mark-read) and every 60s.
  // No realtime subscription in Commit 1 — that arrives with push in Commit 2.
  useEffect(() => {
    if (!authUser?.id) return;
    const handleNotificationsUpdated = () => loadUnread();
    window.addEventListener('ournet:notifications-updated', handleNotificationsUpdated);
    const intervalId = setInterval(loadUnread, 60000);
    return () => {
      window.removeEventListener('ournet:notifications-updated', handleNotificationsUpdated);
      clearInterval(intervalId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authUser?.id]);

  // Mirror the unread count onto the home-screen app-icon badge while the app is
  // open (Commit 2). setAppBadge(n) when >0, clearAppBadge() at zero — guarded
  // inside updateAppBadge for platforms without the Badging API.
  useEffect(() => {
    updateAppBadge(unreadCount);
  }, [unreadCount]);

  const handleBellClick = () => setShowNotifications(v => !v);

  // A logged-out visitor is NOT null: App.js installs a truthy guest object with id: null
  // (App.js guest path and failed-session catch path), so `authUser &&` is not an auth test.
  // One predicate for every signed-in decision in this header — the bell (Architecture.md
  // § Notifications: logged-in only), the identity button's icon / click / title, and the
  // locality selector's guest mode. Signed out, the identity button shows LogIn and opens the
  // login modal directly. Brief: docs/briefs/2026-09-26-guest-object-signed-in-test.md.
  const signedIn = isSignedIn(authUser);
  const showBell = signedIn;

  // Bell + unread pill — shared across the desktop (absolute) and mobile (flex) clusters.
  // Presentational NotificationsBell; this component keeps the unread fetch.
  const renderBell = () => (
    <NotificationsBell
      unreadCount={unreadCount}
      onClick={handleBellClick}
      title={t('notifications:inbox_title')}
    />
  );

  const loadUserAreas = async (eventUserId = null, currentSelectedArea = null) => {
    try {
      setLoading(true);

      // Use userId from event (if provided) or from authUser context
      // Event userId is used when React state hasn't propagated yet (e.g., right after login)
      const userId = eventUserId || authUser?.id;

      // Use passed selectedArea or fall back to prop (for closure freshness)
      const effectiveSelectedArea = currentSelectedArea || selectedArea;

      // Skip loading areas for guests (no user ID)
      if (!userId) {
        console.log('HeaderButtonsFeed: Guest user - no areas to load');
        setUserAreas([]);
        setLoading(false);
        return;
      }

      console.log('HeaderButtonsFeed: Loading areas for authenticated user:', userId);

      // Load all user's areas using direct fetch (Supabase client can hang)
      const { data: areas, error: areasError } = await supabaseFetch.query('user_areas', {
        select: '*',
        eq: { user_id: userId },
        order: 'created_at.asc'
      });

      if (areasError) {
        console.error('Error loading areas:', areasError);
        setLoading(false);
        return;
      }

      console.log('HeaderButtonsFeed: Loaded areas:', areas?.length || 0);
      setUserAreas(areas || []);

      // If App.js already has a selectedArea (from previous navigation), use it
      // Otherwise, select the home area on initial load
      console.log('🔍 loadUserAreas decision: effectiveSelectedArea=', effectiveSelectedArea, 'hasInitializedArea=', hasInitializedArea.current);

      if (effectiveSelectedArea?.id) {
        // App.js has a selected area - just load its localities for display
        console.log('HeaderButtonsFeed: Using existing selectedArea from App.js:', effectiveSelectedArea.display_name);
        hasInitializedArea.current = true;
        await loadAreaLocalities(effectiveSelectedArea.id, effectiveSelectedArea.display_name);
      } else if (!hasInitializedArea.current) {
        // No selectedArea in App.js yet - select home area
        const homeArea = (areas || []).find(a => a.is_home);
        const areaToLoad = homeArea || areas?.[0];

        if (areaToLoad) {
          console.log('HeaderButtonsFeed: Initial load - selecting area:', areaToLoad.display_name);
          hasInitializedArea.current = true;
          if (setSelectedArea) {
            setSelectedArea(areaToLoad);
          }
          await loadAreaLocalities(areaToLoad.id, areaToLoad.display_name);

          // Dispatch area-changed event so App.js loads articles for this area
          window.dispatchEvent(new CustomEvent('ournet:area-changed', {
            detail: {
              areaId: areaToLoad.id,
              areaCode: areaToLoad.area_code,
              displayName: areaToLoad.display_name
            }
          }));
        } else {
          console.log('HeaderButtonsFeed: No areas found for user');
        }
      } else {
        console.log('HeaderButtonsFeed: Preserving current area selection');
      }
    } catch (err) {
      console.error('Error loading user areas:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadAreaLocalities = async (areaId, areaDisplayName = '') => {
    // Skip if we're already loading this exact area (prevents duplicate calls from race conditions)
    if (loadingAreaIdRef.current === areaId) {
      console.log('HeaderButtonsFeed: Already loading area', areaId, '- skipping duplicate');
      return;
    }
    loadingAreaIdRef.current = areaId;

    try {
      // Clear previous locality name immediately to prevent stale display
      setHomeLocalityName('');

      // Fetch the area to get primary_boundary_id and boundary_ids
      const { data: areaData, error: areaError } = await supabaseFetch.query('user_areas', {
        select: 'primary_boundary_id,boundary_ids',
        eq: { id: areaId },
        single: true
      });

      // Track the boundary's human-readable name for fallback display
      let boundaryName = '';

      if (!areaError && areaData) {
        // The member's own parish: the shared rule (base parish, else the first saved boundary).
        const headlineBoundaryId = baseParishId(areaData);

        if (headlineBoundaryId) {
          // Shared lookup (countriesService) — App.js reads the same row for the country in view.
          const place = await getBoundaryPlace(headlineBoundaryId);
          if (place?.name) {
            boundaryName = place.name;
          }
        }
      }

      // Use direct fetch (Supabase client can hang)
      const { data, error } = await supabaseFetch.query('user_area_localities', {
        select: 'id,locality_id,display_order,locality:localities(id,name,municipality,latitude,longitude)',
        eq: { area_id: areaId },
        order: 'display_order.asc'
      });

      if (error) {
        console.error('Error querying area localities:', error);
        // Use area display_name as fallback on error
        if (areaDisplayName) {
          setHomeLocalityName(areaDisplayName);
        }
        return;
      }

      console.log('HeaderButtonsFeed: Raw localities data:', data);

      // Transform to match LocalitySelector expected format
      const localities = (data || []).map(item => ({
        id: item.locality?.id,
        name: item.locality?.name || 'Unknown',
        municipality: item.locality?.municipality,
        latitude: item.locality?.latitude,
        longitude: item.locality?.longitude,
        display_order: item.display_order
      }));

      console.log('HeaderButtonsFeed: Transformed localities:', localities);
      setAreaLocalities(localities);

      // Priority: boundary name (saved primary_boundary_id) → legacy locality → area display_name
      // Boundary name is the authoritative saved location; legacy localities may be IP-derived
      const homeLocality = localities[0];
      if (boundaryName) {
        setHomeLocalityName(boundaryName);
        console.log('HeaderButtonsFeed: Using boundary name (primary):', boundaryName);
      } else if (homeLocality) {
        setHomeLocalityName(homeLocality.name);
        console.log('HeaderButtonsFeed: Fallback to legacy locality:', homeLocality.name);
      } else if (areaDisplayName) {
        setHomeLocalityName(areaDisplayName);
        console.log('HeaderButtonsFeed: Fallback to area display_name:', areaDisplayName);
      }
    } catch (err) {
      console.error('Error loading area localities:', err);
      // Use area display_name as fallback on exception
      if (areaDisplayName) {
        setHomeLocalityName(areaDisplayName);
      }
    } finally {
      // Clear loading ref so future loads of this area still work (e.g. switching away and back)
      if (loadingAreaIdRef.current === areaId) {
        loadingAreaIdRef.current = null;
      }
    }
  };

  // Determine if we're in locality or groups context
  const isLocalityContext = currentContext?.type === 'location' || currentContext?.id === 'locality';
  const isGroupsContext = currentContext?.type === 'group' || currentContext?.id === 'groups';

  // Article/event creation is not available while a temporary visit (Change 3) is active —
  // addendum-1 §A/§C. Chat is deliberately excluded (chat targets the visited locality instead).
  // Groups-context creation is a structurally separate path (always carries groupId) and is
  // unaffected regardless of visit state.
  const isCreateHiddenForVisit = isLocalityContext && !!visitArea && (currentMode === 'news' || currentMode === 'events');


  const handleProfileClick = () => {
    console.log('Profile button clicked');
    // Reached only when signed in: every identity button routes a signed-out click to
    // goToLogin instead, so a guest never sees ProfileMenu. Its Install item is offered to
    // signed-out visitors by the login modal's own "Install the app" link instead (same
    // useInstallPrompt canInstall/install). Individual menu items handle their own auth checks.
    setShowProfileMenu(!showProfileMenu);
  };

  const handleAdminClick = () => {
    console.log('Admin button clicked');
    window.dispatchEvent(new CustomEvent('navigate-to-admin'));
  };

  // Handle article search (in Locality context)
  const handleArticleSearch = (searchTerm) => {
    console.log('🔍 Article search triggered:', searchTerm);
    window.dispatchEvent(new CustomEvent('ournet:search-articles', {
      detail: { searchTerm }
    }));
  };

  // Handle group search (in Groups context, GroupsGrid view)
  const handleGroupSearch = (searchTerm) => {
    console.log('🔍 Group search triggered:', searchTerm);
    window.dispatchEvent(new CustomEvent('ournet:search-groups', {
      detail: { searchTerm }
    }));
  };

  // Handle Create button click - context and mode aware
  const handleCreateClick = () => {
    console.log('Create button clicked - mode:', currentMode, 'context:', currentContext);

    // Check if user is authenticated first
    if (!authUser?.id) {
      requireAuth('gate_body_generic', () => {
        // After login, trigger the create action
        triggerCreateAction();
      });
      return;
    }

    triggerCreateAction();
  };

  const triggerCreateAction = () => {
    // Groups context: Create Group
    if (isGroupsContext && !selectedGroup) {
      requireAuth('gate_body_create_group', () => {
        window.dispatchEvent(new CustomEvent('ournet:navigate', {
          detail: { view: 'new-group', mode: 'add' }
        }));
      });
      return;
    }

    // Otherwise, create based on current mode (news, events, chat)
    switch (currentMode) {
      case 'news':
        if (isCreateHiddenForVisit) return; // article creation unavailable while visiting — belt-and-braces alongside the hidden button
        requireAuth('gate_body_post', () => {
          // Mobile → Walking Composer, in both locality and group context.
          // Group context threads groupId so the composer can anchor to the
          // group instead of GPS (see WalkingComposer's isGroupMode).
          if (isMobile) {
            window.dispatchEvent(new CustomEvent('ournet:navigate', {
              detail: {
                view: 'walking-composer',
                groupId: isGroupsContext ? selectedGroup?.id : undefined
              }
            }));
          } else {
            window.dispatchEvent(new CustomEvent('ournet:navigate', {
              detail: {
                view: 'new-article',
                mode: 'add',
                groupId: selectedGroup?.id,
                groupName: selectedGroup?.name
              }
            }));
          }
        });
        break;

      case 'events':
        if (isCreateHiddenForVisit) return; // event creation unavailable while visiting — belt-and-braces alongside the hidden button
        requireAuth('gate_body_add_event', () => {
          window.dispatchEvent(new CustomEvent('ournet:navigate', {
            detail: {
              view: 'new-event',
              mode: 'add',
              groupId: selectedGroup?.id,
              groupName: selectedGroup?.name
            }
          }));
        });
        break;

      case 'chat':
        requireAuth('gate_body_create_chat', () => {
          window.dispatchEvent(new CustomEvent('ournet:navigate', {
            detail: {
              view: 'new-chat',
              mode: 'add',
              groupId: selectedGroup?.id,
              groupName: selectedGroup?.name
            }
          }));
        });
        break;

      default:
        console.log('No create action defined for mode:', currentMode);
    }
  };

  const handleSelectArea = async (area) => {
    console.log('Area selected:', area);

    // Selecting any of the user's own area slots ends an active visit (addendum-4 §2) — this is
    // now the only way a visit ends besides reload/login/logout, replacing the retired separate
    // "Return home" control. Cleared before the area-changed dispatch below so App.js's listener
    // reads visitAreaRef.current as already null and reloads using this area's own boundary ids.
    onClearVisit?.();

    // Close the selector
    setShowLocalitySelector(false);
    setShowLocalityButton(false);

    // Set the new selected area in App.js state (persists across navigation)
    if (setSelectedArea) {
      setSelectedArea(area);
    }

    // Load localities for the new area and set home locality name
    await loadAreaLocalities(area.id, area.display_name);

    // Dispatch event to notify App.js to reload content for this area
    window.dispatchEvent(new CustomEvent('ournet:area-changed', {
      detail: {
        areaId: area.id,
        areaCode: area.area_code,
        displayName: area.display_name
      }
    }));
  };

  // Top region of the dial's centre button (guest's single circle, and the logged-in dial's top
  // ~3/5 split, Change 3) — opens the shared mode="browse" picker. Which destination the picker's
  // selection reaches (guest's own boundary vs a logged-in visit) is decided in
  // handleQuickPickerSelect below, not here — this click handler is identical for both.
  const handleChangeLocationClick = () => {
    console.log('Change Location clicked');

    setShowLocalitySelector(false);
    setShowLocalityButton(false);
    setShowQuickPicker(true);
  };

  // Bottom region of the logged-in dial's centre button (~2/5 split, Change 3) — unchanged
  // "Manage Locations" behaviour, navigates to My Locations. Guests never render this region.
  const handleManageLocationsClick = () => {
    console.log('Manage Locations clicked');

    setShowLocalitySelector(false);
    setShowLocalityButton(false);

    window.dispatchEvent(new CustomEvent('ournet:navigate', {
      detail: { view: 'locations' }
    }));
  };

  const handleQuickPickerSelect = (selection) => {
    setShowQuickPicker(false);
    if (authUser?.id) {
      // Logged-in: enters a temporary visit (Change 3) — session-only, never touches user_areas.
      onEnterVisit?.(selection);
    } else if (onGuestLocationChange) {
      onGuestLocationChange(selection);
    }
  };

  // Visiting-indicator text, shared by all three branches below (mobile front-page, mobile-
  // legacy, desktop) — addendum-4 §2 retires the separate orange banner in favour of
  // recolouring the existing Change 2 header trigger itself. No flag, no mode suffix while
  // visiting — the locality-name segment is replaced outright, identically in all three branches
  // (Advisor consultation flagged the risk of three independently-decided formats).
  const visitingIndicatorText = (locality) => tLoc('visiting_indicator', { locality: locality || '' });

  // Get display name for header (used by both mobile and desktop)
  const getDisplayName = () => {
    if (isGroupsContext) {
      return selectedGroup?.name || t('my_groups');
    }
    const locationName = homeLocalityName || selectedArea?.display_name || currentContext?.name || t('my_location');
    const flag = getCountryFlag(countryInView);
    return `${flag} ${locationName}`;
  };

  // Location name only (no flag) — for mobile front-page header where LanguageSelector shows the flag
  const getLocationName = () => {
    return homeLocalityName || selectedArea?.display_name || currentContext?.name || t('my_location');
  };

  // Get Create button label based on context and mode
  const getCreateButtonLabel = () => {
    // Groups context (no group selected): Create Group
    if (isGroupsContext && !selectedGroup) {
      return t('groups:create_group');
    }

    // Otherwise based on mode
    switch (currentMode) {
      case 'news':
        // Mobile → Walking mode label (locality and group context both route to the Walking Composer)
        if (isMobile) {
          return t('walking:entry.button');
        }
        return t('create_article');
      case 'events':
        return t('events:create_event');
      case 'chat':
        return t('common:create_new_chat');
      default:
        return t('create_article'); // Default to article
    }
  };

  // Get mode display name (convert "News" to "Articles/News" in news mode)
  const getModeDisplayName = () => {
    if (currentMode === 'news') {
      return `${t('articles') || 'Articles'}/${t('mode_news') || 'News'}`;
    }
    return currentMode ? t(`mode_${currentMode}`) : '';
  };

  // ============================================
  // MOBILE LAYOUT (viewport < 1200px)
  // ============================================

  // The Locality front page's per-screen create+search action line was retired from here
  // (mobile-tab-subheaders brief) — the tab bar below is navigation only. Each panel
  // (DirectoryColumn/MobileFrontPage's news case/ChatColumn/EventsColumn/GroupsColumn) now
  // owns its own pinned sub-header, reusing its own existing create handler rather than a
  // parallel per-screen switch living in shared chrome. The group front-page's create action +
  // label likewise live in MobileGroupFrontPage (Mobile Groups Commit 6 fix, unchanged).

  // Mode tabs for the five-tab mode bar (locked order: Directory first)
  const MOBILE_SCREENS = [
    { id: 'directory', label: tFp('mobile_screen_directory') || 'Directory', icon: Book },
    { id: 'news', label: tFp('mobile_screen_news') || 'News', icon: Newspaper },
    { id: 'chat', label: tFp('mobile_screen_chat') || 'Chat', icon: MessageCircle },
    { id: 'events', label: tFp('mobile_screen_events') || 'Events', icon: Calendar },
    { id: 'groups', label: tFp('mobile_screen_groups') || 'Groups', icon: Users }
  ];

  if (isMobile) {
    // ────────────────────────────────────────────
    // MOBILE LOCALITY FRONT PAGE HEADER (new design)
    // ────────────────────────────────────────────
    const isMobileFP = isLocalityContext && frontPageActive;

    if (isMobileFP) {
      const contextColor = theme?.primary || tokens.colors.contextLocality;

      // The Groups tab's own content (GroupsColumn.jsx) deliberately sources mauve directly
      // from `themes.groups.primary` rather than the ambient theme — "mauve always means
      // Groups" holds even while still inside the Locality composite (its own comment: "this
      // column is mounted while the ambient ThemeProvider is still 'locality'... the colour
      // system is semantic here"). The tab bar itself is ambient-blue throughout (ThemeProvider
      // doesn't switch until a group is actually entered), so the Groups tab, when active,
      // needs its OWN accent to match the mauve content below it — everything else (Row 1,
      // the other four tabs) stays locality blue. Addendum 1, defect 2: without Row 3's white
      // buffer between the tab bar and each panel's sub-header, an active-blue Groups tab
      // sitting directly above GroupsColumn's mauve sub-header read as a colour band.
      const tabAccentColor = (screenId) => screenId === 'groups' ? tokens.colors.contextGroups : contextColor;

      // Single source of truth for tab appearance (per locked decision: keep active-tab
      // styling in one place so a later "growing active tab" variant is a one-spot change).
      // CSS-only bleed: the active tab drops its bottom border so its white runs straight into
      // the panel's own sub-header below — which now carries its own top border in the same
      // colour/width (MobileActionLine's topBorder), framing the two as one continuous region
      // (mobile-tab-subheaders addendum 2). No tab, active or inactive, carries a bottom border
      // of its own any more — that line comes from the sub-header's side, once, not doubled.
      const tabStyle = (active, screenId) => {
        const accent = tabAccentColor(screenId);
        return {
          flex: 1,
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '2px',
          cursor: 'pointer',
          boxSizing: 'border-box',
          fontFamily: tokens.typography.fontFamily,
          background: active ? 'white' : tokens.colors.headerBackground,
          borderTop: active ? `2px solid ${accent}` : '2px solid transparent',
          borderLeft: active ? `2px solid ${accent}` : 'none',
          borderRight: active ? `2px solid ${accent}` : 'none',
          borderBottom: 'none',
          // Rounded tops only — reads as a proper tab; bottoms stay square (8px = app card radius)
          borderTopLeftRadius: '8px',
          borderTopRightRadius: '8px'
        };
      };

      return (
        <>
          <header style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            height: '94px',    // Row 1 (50) + tab bar (44) — the tab bar is navigation only; each
                                // panel now renders its own pinned sub-header inside its own body
                                // (mobile-tab-subheaders brief, retiring the old Row 3 action line)
            backgroundColor: 'white',
            zIndex: 1000
            // No boxShadow: it used to separate the header block from the content below (a soft
            // grey drop-shadow bleeding a few px past the header's own bottom edge, painted at
            // this element's z-index so it shows on top of whatever's beneath it). That's now
            // the opposite of the intended design — the active tab and its sub-header/viewport
            // are meant to read as one seamless, unbroken shape (mobile-tab-subheaders addenda
            // 2-4) — and the shadow was landing exactly in the notch (addendum 3) meant to be
            // genuinely empty, showing as a residual grey line there (addendum 4).
          }}>
            {/* Row 1 (50px): Context chip (left) · Location name (screen-centred) · Language + Profile (right) */}
            <div style={{
              height: '50px',
              display: 'flex',
              alignItems: 'center',
              padding: '0 12px',
              borderBottom: `1px solid ${tokens.colors.disabledBackground}`
            }}>
              {/* Left: logo + context chip (the OTHER context — Groups/mauve here). Shared cluster,
                  so the logo + pill are dimensionally identical to the detail header's. */}
              <MobileHeaderLeftCluster
                pillLabel={t('groups')}
                pillColor={tokens.colors.contextGroups}
                onPillClick={() => onContextSwitch?.('groups')}
              />

              {/* Centre: Location name — flex-centred in the space BETWEEN the left cluster and the
                  right controls (not absolute-on-full-width, which read off-centre). Tap → selector. */}
              <div
                onClick={() => setShowLocalitySelector(!showLocalitySelector)}
                style={{
                  flex: 1,
                  minWidth: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '4px',
                  padding: '0 8px',
                  cursor: 'pointer'
                }}
              >
                <span style={{
                  fontSize: '15px',
                  fontWeight: 'bold',
                  color: visitArea ? tokens.colors.visiting : contextColor,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}>
                  {visitArea ? visitingIndicatorText(visitArea.displayName) : getLocationName()}
                </span>
                {(
                  <ChevronDown
                    size={16}
                    color={visitArea ? tokens.colors.visiting : contextColor}
                    strokeWidth={3}
                    style={{
                      transition: 'transform 0.2s',
                      transform: showLocalitySelector ? 'rotate(180deg)' : 'rotate(0deg)',
                      flexShrink: 0
                    }}
                  />
                )}
              </div>

              {/* Right: Notifications bell (logged-in) + Language selector + Profile/Login */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                flexShrink: 0
              }}>
                {showBell && renderBell()}
                <LanguageSelector />
                <StandardButton
                  variant="identity"
                  iconName={signedIn ? 'User' : 'LogIn'}
                  profileImage={authUser?.avatar_url || null}
                  onClick={signedIn ? handleProfileClick : goToLogin}
                  title={signedIn ? '' : t('auth:log_in')}
                />
              </div>
            </div>

            {/* Row 2 (44px): five-tab mode bar — Directory · News · Chat · Events · Market.
                Equal width, fixed size; tapping a tab sets mobileScreen. No tab carries a bottom
                border — the panel's own sub-header below frames the join instead, once, in the
                active tab's own colour (see tabStyle's own note, addendum 2). */}
            <div style={{
              height: '44px',
              display: 'flex',
              alignItems: 'stretch'
            }}>
              {MOBILE_SCREENS.map(screen => {
                const active = screen.id === mobileScreen;
                const Icon = screen.icon;
                const tabColor = active ? tabAccentColor(screen.id) : tokens.colors.textSecondary;
                return (
                  <button
                    key={screen.id}
                    onClick={() => setMobileScreen(screen.id)}
                    style={tabStyle(active, screen.id)}
                  >
                    <Icon size={16} strokeWidth={2} color={tabColor} />
                    <span style={{
                      fontSize: '9px',
                      fontWeight: active ? 700 : 500,
                      color: tabColor,
                      letterSpacing: '0.2px',
                      maxWidth: '100%',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}>
                      {screen.label}
                    </span>
                  </button>
                );
              })}
            </div>

          </header>

          {/* LocalitySelector dropdown */}
          {showLocalitySelector && (
            <>
              <div
                onClick={() => setShowLocalitySelector(false)}
                style={{
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  zIndex: 1001
                }}
              />
              <div style={{
                position: 'fixed',
                top: '94px',
                left: '50%',
                transform: 'translateX(-50%)',
                zIndex: 1002
              }}>
                <LocalitySelector
                  areas={userAreas}
                  onSelectArea={handleSelectArea}
                  onChangeLocation={handleChangeLocationClick}
                  onManageLocations={handleManageLocationsClick}
                  isGuest={!signedIn}
                />
              </div>
            </>
          )}

          {/* Map location picker modal (guest browse / logged-in visit) */}
          {showQuickPicker && (
            <MapLocationPicker
              mode="browse"
              onSelect={handleQuickPickerSelect}
              onClose={() => setShowQuickPicker(false)}
              // 2026-09-02 country-selection foundation build: routed through the shared
              // IP-cache reader + default-country chain, replacing the inline localStorage-
              // parsing IIFEs (docs/reuse-backlog.md 2026-09-02 entry).
              detectedBoundaryId={getCachedIpLocation()?.boundaryId || null}
              countryCode={resolveDefaultCountry({ ipCountryCode: getCachedIpLocation()?.countryCode })}
            />
          )}

          <ProfileMenu
            isOpen={showProfileMenu}
            onClose={() => setShowProfileMenu(false)}
            currentUser={authUser || currentUser}
          />
          <NotificationsDropdown
            isOpen={showNotifications}
            onClose={() => setShowNotifications(false)}
            currentUser={authUser}
          />
        </>
      );
    }

    // ────────────────────────────────────────────
    // MOBILE LEGACY HEADER (Groups context, or locality with frontPageActive=false)
    // ────────────────────────────────────────────
    // Mobile group front page (Approach B): MobileGroupFrontPage owns the chrome — the persistent
    // identity card carries the group name, the framed tabs carry mode switching, and each group
    // column owns its own "+". So the header collapses to a plain 50px row: logo + language/profile,
    // no create/search line, no mode suffix, no centre name (it would duplicate the card directly
    // below). The locality five-tab/front-page header treatment is unaffected (isLocalityContext).
    const isMobileGroupFP = isGroupsContext && !!selectedGroup && frontPageActive;
    const showCreateAndSearch = !isMobileGroupFP && (currentMode === 'news' || currentMode === 'events' || currentMode === 'chat' || (isGroupsContext && !selectedGroup));
    // Group FP header is a plain 50px Row 1; its create action line lives UNDER the tabs in
    // MobileGroupFrontPage (matching locality's tabs → action line → body stack), not here.
    const headerHeight = showCreateAndSearch ? '90px' : '50px';

    return (
      <>
        <header style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          height: headerHeight,
          backgroundColor: 'white',
          zIndex: 1000,
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}>
          {/* Line 1: Logo, Location/Group name, Profile/Login */}
          <div style={{
            height: '50px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '0 12px',
            borderBottom: showCreateAndSearch ? '1px solid #E5E7EB' : 'none'
          }}>
          {/* Left: OurNet logo + context-toggle chip (the OTHER context). Shared cluster — identical
              dimensions to the locality and detail headers (no logo/pill drift). */}
          <MobileHeaderLeftCluster
            pillLabel={isLocalityContext ? t('groups') : t('locality')}
            pillColor={isLocalityContext ? tokens.colors.contextGroups : tokens.colors.contextLocality}
            onPillClick={() => onContextSwitch?.(isLocalityContext ? 'groups' : 'locality')}
          />

          {/* Center: Location/Group name + mode — flex-centred in the space BETWEEN the left
              cluster and the right controls (not space-between, which read off-centre with the
              wider left cluster). Matches the locality front-page header's centring. */}
          <div
            onClick={() => isLocalityContext && setShowLocalitySelector(!showLocalitySelector)}
            style={{
              flex: 1,
              minWidth: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '0 8px',
              cursor: isLocalityContext ? 'pointer' : 'default'
            }}
          >
            <span style={{
              fontSize: '14px',
              fontWeight: 'bold',
              color: (isLocalityContext && visitArea) ? tokens.colors.visiting : (theme?.primary || (isLocalityContext ? '#024B99' : '#BB4D9A')),
              maxWidth: '180px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}>
              {isMobileGroupFP ? '' : (isLocalityContext && visitArea ? visitingIndicatorText(visitArea.displayName) : `${getDisplayName()} - ${getModeDisplayName()}`)}
            </span>
            {isLocalityContext && (
              <ChevronDown
                size={16}
                color={visitArea ? tokens.colors.visiting : (theme?.primary || '#024B99')}
                strokeWidth={3}
                style={{
                  transition: 'transform 0.2s',
                  transform: showLocalitySelector ? 'rotate(180deg)' : 'rotate(0deg)',
                  marginTop: '-2px'
                }}
              />
            )}
          </div>

          {/* Right: Notifications bell (logged-in) + Language selector + Profile/Login button */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            flexShrink: 0
          }}>
            {showBell && renderBell()}
            <LanguageSelector />
            <StandardButton
              variant="identity"
              iconName={signedIn ? 'User' : 'LogIn'}
              profileImage={authUser?.avatar_url || null}
              onClick={signedIn ? handleProfileClick : goToLogin}
              title={signedIn ? '' : t('auth:log_in')}
            />
          </div>
          </div>

          {/* Line 2: Create button + Search (context and mode aware) */}
          {showCreateAndSearch && (
            <div style={{
              height: '40px',
              display: 'flex',
              alignItems: 'center',
              padding: '0 12px',
              gap: '8px',
              position: 'relative'
            }}>
              {!isCreateHiddenForVisit && (
                <StandardButton
                  variant="action"
                  iconName="Plus"
                  label={getCreateButtonLabel()}
                  onClick={handleCreateClick}
                />
              )}

              {/* AnimatedSearchBar — hidden in demo mode (hideSearch): search only sees public */}
              {!hideSearch && <AnimatedSearchBar
                context={isLocalityContext ? 'locality' : 'groups'}
                onSearch={isLocalityContext ? handleArticleSearch : handleGroupSearch}
                position={{ right: '0px', top: '4px' }}
                placeholder={
                  isGroupsContext && !selectedGroup
                    ? (t('search_groups') || 'Search groups...')
                    : (t('search_articles') || 'Search articles...')
                }
                zIndex={100}
              />}
            </div>
          )}
        </header>

        {/* LocalitySelector dropdown - mobile positioning */}
        {isLocalityContext && showLocalitySelector && (
          <>
            <div
              onClick={() => setShowLocalitySelector(false)}
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 1001
              }}
            />
            <div style={{
              position: 'fixed',
              top: showCreateAndSearch ? '95px' : '55px',
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 1002
            }}>
              <LocalitySelector
                areas={userAreas}
                onSelectArea={handleSelectArea}
                onChangeLocation={handleChangeLocationClick}
                onManageLocations={handleManageLocationsClick}
                isGuest={!signedIn}
              />
            </div>
          </>
        )}

        {/* Map location picker modal (guest browse / logged-in visit) */}
        {showQuickPicker && (
          <MapLocationPicker
            mode="browse"
            onSelect={handleQuickPickerSelect}
            onClose={() => setShowQuickPicker(false)}
            // 2026-09-02 country-selection foundation build: routed through the shared
            // IP-cache reader + default-country chain, replacing the inline localStorage-
            // parsing IIFEs (docs/reuse-backlog.md 2026-09-02 entry).
            detectedBoundaryId={getCachedIpLocation()?.boundaryId || null}
            countryCode={resolveDefaultCountry({ ipCountryCode: getCachedIpLocation()?.countryCode })}
          />
        )}

        <ProfileMenu
          isOpen={showProfileMenu}
          onClose={() => setShowProfileMenu(false)}
          currentUser={authUser || currentUser}
        />
        <NotificationsDropdown
          isOpen={showNotifications}
          onClose={() => setShowNotifications(false)}
          currentUser={authUser}
        />
      </>
    );
  }

  // ============================================
  // DESKTOP LAYOUT (viewport >= 1200px)
  // ============================================
  // Layering architecture: position fixed, overlays HeaderContainer
  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      height: '80px',
      zIndex: 101,
      pointerEvents: 'none'
    }}>
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: '60px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        {/* Context Switch Buttons - Two side-by-side pills, active one colored, inactive greyed out */}
      <div style={{
        position: 'absolute',
        left: '105px',
        top: '12px',
        display: 'flex',
        pointerEvents: 'auto'
      }}>
        {/* Locality Button */}
        <button
          onClick={() => { if (!isLocalityContext) onContextSwitch('locality'); }}
          style={{
            width: '110px',
            height: '40px',
            background: isLocalityContext ? tokens.colors.contextLocality : `${tokens.colors.contextLocality}40`,
            color: isLocalityContext ? 'white' : '#1A1A1A',
            border: 'none',
            borderRadius: '20px 0 0 20px',
            fontSize: '14px',
            fontWeight: 'bold',
            cursor: isLocalityContext ? 'default' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'background 0.2s, opacity 0.2s'
          }}
          onMouseOver={(e) => { if (!isLocalityContext) e.currentTarget.style.opacity = '0.85'; }}
          onMouseOut={(e) => e.currentTarget.style.opacity = '1'}
        >
          {t('locality')}
        </button>
        {/* Groups Button */}
        <button
          onClick={() => onContextSwitch('groups')}
          style={{
            width: '110px',
            height: '40px',
            background: isGroupsContext ? tokens.colors.contextGroups : `${tokens.colors.contextGroups}40`,
            color: isGroupsContext ? 'white' : '#1A1A1A',
            border: 'none',
            borderRadius: '0 20px 20px 0',
            fontSize: '14px',
            fontWeight: 'bold',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'background 0.2s, opacity 0.2s'
          }}
          onMouseOver={(e) => e.currentTarget.style.opacity = '0.85'}
          onMouseOut={(e) => e.currentTarget.style.opacity = '1'}
        >
          {t('groups')}
        </button>
      </div>

      {/* Context Name Display - Centered 180px right of window center */}
      {/* On Locality front page: locality name only, no mode suffix */}
      {/* Otherwise: "Title - Mode" format (e.g., "São Martinho - Articles", "Wine Lovers - Chat") */}
      <div
        onClick={() => isLocalityContext && setShowLocalitySelector(!showLocalitySelector)}
        style={{
          position: 'absolute',
          left: 'calc(50% + 180px)',
          transform: 'translateX(-50%)',
          fontSize: `${desktopTitleFontSize}px`,
          fontWeight: 'bold',
          color: (isLocalityContext && visitArea) ? tokens.colors.visiting : (theme?.primary || '#024B99'),
          cursor: isLocalityContext ? 'pointer' : 'default',
          pointerEvents: 'auto'
        }}
      >
        {(() => {
          // While visiting, the locality-name segment is replaced outright — no flag, no mode
          // suffix — identically to the mobile branches (addendum-4 §2). Guarded on
          // isLocalityContext so a visit never overwrites the Groups header's own identity.
          if (isLocalityContext && visitArea) {
            return visitingIndicatorText(visitArea.displayName);
          }

          // Determine the base title
          let title;
          if (isLocalityContext) {
            title = homeLocalityName || selectedArea?.display_name || currentContext?.name || 'OurNet';
          } else if (isGroupsContext && selectedGroup) {
            title = selectedGroup.name;
          } else {
            title = currentContext?.name || 'OurNet';
          }

          // On Locality front page (desktop), show the locality name — no mode suffix
          if (isLocalityContext && !isMobile) {
            return title;
          }

          // On the Group front page (desktop), show the group name — no mode suffix. currentMode
          // still carries 'news' under the front page, so without this guard the title leaked a
          // stale "- Articles/News" suffix over GroupFrontPage (Groups Tier 1, Commit 4).
          if (!isMobile && isGroupsContext && selectedGroup && frontPageActive) {
            return title;
          }

          // Get translated mode display name (uses "Articles" instead of "News")
          const modeDisplayName = getModeDisplayName();

          // Append " - Mode" if we have a mode
          if (modeDisplayName) {
            return `${title} - ${modeDisplayName}`;
          }
          return title;
        })()}
      </div>

      {/* Create Button - Desktop, aligned with first card in NewsGrid (sidebar width + 20px) */}
      {/* Hidden on Locality desktop front page — each column has its own "New" button */}
      {/* Visible in locality context when frontPageActive is false (i.e. NewsGrid/mode grid is showing) */}
      {/* Also hidden on the GROUP desktop front page — GroupFrontPage's columns own their "+",
          mirroring the locality suppression (Mobile Groups, Commit 10). */}
      {!isMobile && (!isLocalityContext || !frontPageActive) && !(isGroupsContext && !!selectedGroup && frontPageActive) && (currentMode === 'news' || currentMode === 'events' || currentMode === 'chat' || (isGroupsContext && !selectedGroup)) && (
        <div style={{
          position: 'absolute',
          left: `${sidebarWidth + 20}px`,
          top: '12px',
          pointerEvents: 'auto'
        }}>
          {/* ORIGINAL: + character for create icon
          <button
            onClick={handleCreateClick}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              height: '40px',
              padding: '0 20px',
              background: theme.primary,
              color: 'white',
              border: 'none',
              borderRadius: '20px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'opacity 0.2s',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              whiteSpace: 'nowrap'
            }}
            onMouseOver={(e) => e.currentTarget.style.opacity = '0.9'}
            onMouseOut={(e) => e.currentTarget.style.opacity = '1'}
          >
            <span style={{ fontSize: '18px', fontWeight: 'bold' }}>+</span>
            <span>{getCreateButtonLabel()}</span>
          </button>
          */}
          {/* ORIGINAL: inline create button — replaced with StandardButton action variant */}
          {!isCreateHiddenForVisit && (
            <StandardButton
              variant="action"
              iconName="Plus"
              label={getCreateButtonLabel()}
              onClick={handleCreateClick}
            />
          )}
        </div>
      )}

      {/* Down Arrow - Appears under headline in Locality context (hidden on front page).
          isMobile is always false here (this whole block sits in the desktop-only branch) —
          a stray leftover from before the mobile/desktop split, orphaning this trigger; removed. */}
      {isLocalityContext && (
        <div
          style={{
            position: 'absolute',
            top: '37px', // Just under the 20px headline
            left: 'calc(50% + 180px)',
            transform: 'translateX(-50%)',
            cursor: 'pointer',
            zIndex: 10000,
            pointerEvents: 'auto'
          }}
          onMouseEnter={() => setShowLocalityButton(true)}
          onMouseLeave={() => !showLocalitySelector && setShowLocalityButton(false)}
          onClick={() => setShowLocalitySelector(!showLocalitySelector)}
        >
          {/* Arrow Icon */}
          <ChevronDown size={20} color={visitArea ? tokens.colors.visiting : '#024B99'} strokeWidth={3} />

          {/* My Locations text on hover - covers the arrow */}
          {showLocalityButton && !showLocalitySelector && (
            <div
              style={{
                position: 'absolute',
                top: '2px',
                left: '50%',
                transform: 'translateX(-50%)',
                background: 'white',
                color: '#024B99',
                border: '1px solid #024B99',
                padding: '1px 12px',
                borderRadius: '8px',
                fontSize: '12px',
                fontWeight: '500',
                whiteSpace: 'nowrap',
                height: '15px',
                lineHeight: '13px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              {t('my_locations')}
            </div>
          )}
        </div>
      )}


      {/* Admin Issues Badge - Shows count of unreviewed issues (sys-admins only, only when > 0).
          Leftmost in the row, clear of the Shield (130px) it used to sit on: left of the search
          circle (240–280px) on Locality — the search flyout covers it when open — and in the
          search circle's slot elsewhere, where there is no search circle beside the bell.
          (docs/briefs/2026-09-18-header-icons-and-profile-dial-layout-fixes.md §1) */}
      {isAdmin && issueCount > 0 && (
        <div
          onClick={() => setShowIssuesDropdown(true)}
          style={{
            position: 'absolute',
            right: isLocalityContext ? '300px' : '240px',
            top: '15px',
            background: '#DC2626',
            color: 'white',
            borderRadius: '12px',
            padding: '4px 10px',
            fontSize: '12px',
            fontWeight: 'bold',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            pointerEvents: 'auto',
            boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
            transition: 'transform 0.2s'
          }}
          onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
          onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
        >
          <span>{issueCount}</span>
          <span>{t('issues')}</span>
        </div>
      )}

      {/* Notifications bell - logged-in users only. Left end of the right-edge icon cluster:
          right 120px (non-admin) / 180px (clears the admin Shield at 130px). The locality
          search bar is offset +50px to clear it only while the bell shows (see its position
          above); signed out it closes up. */}
      {!isMobile && showBell && (
        <div style={{
          position: 'absolute',
          right: isAdmin ? '180px' : '120px',
          top: '12px',
          pointerEvents: 'auto'
        }}>
          {renderBell()}
        </div>
      )}

      {/* Animated Search Bar - Context-specific, desktop only */}
      {!isMobile && (
        <>
          {/* Locality context: Search articles by tags */}
          {isLocalityContext && (
            <AnimatedSearchBar
              context="locality"
              onSearch={handleArticleSearch}
              position={{
                // Signed in: clears the notifications bell (bell at 120, or 180 beside the admin
                // Shield). Signed out there is no bell and no space is reserved for it — the
                // search bar closes up to where it sat before the bell existed (LanguageSelector
                // at 80, +40 wide, +10 gap). Brief: docs/briefs/2026-09-26-logged-out-header-gap-and-install-link.md.
                right: showBell ? (isAdmin ? '240px' : '180px') : '130px',
                top: '12px'
              }}
              placeholder={t('search_articles') || 'Search articles...'}
              zIndex={9000}
            />
          )}
          {/* Groups context (GroupsGrid view): Search groups by tags */}
          {isGroupsContext && !selectedGroup && (
            <AnimatedSearchBar
              context="groups"
              onSearch={handleGroupSearch}
              position={{ right: '20px', top: '12px' }}
              placeholder={t('search_groups') || 'Search groups...'}
              zIndex={9000}
            />
          )}
        </>
      )}

      {/* Admin Button - Only visible to is_sys_admin users, positioned left of Language selector */}
      {isAdmin && (
        <div style={{
          position: 'absolute',
          right: '130px', // admin is always signed in (isAdmin resets on logout)
          top: '12px',
          pointerEvents: 'auto'
        }}>
          <div style={{ position: 'relative' }}>
            {/* ORIGINAL: inline admin button — replaced with StandardButton admin variant */}
            <StandardButton
              variant="admin"
              iconName="Shield"
              onClick={handleAdminClick}
              title="Admin Panel"
            />
            {hasAdminAlerts && (
              <span
                style={{
                  position: 'absolute',
                  top: '0px',
                  right: '0px',
                  width: '10px',
                  height: '10px',
                  borderRadius: '50%',
                  backgroundColor: '#E53E3E',
                  border: '2px solid white'
                }}
              />
            )}
          </div>
        </div>
      )}

      {/* Language/Country Selector - Positioned left of Profile/Login button */}
      <div style={{
        position: 'absolute',
        right: '80px', // one position in every state — the identity button is a fixed circle
        top: '18px',
        pointerEvents: 'auto'
      }}>
        <LanguageSelector />
      </div>

      {/* Profile/Login Button - Right side */}
      <div style={{
        position: 'absolute',
        right: '20px',
        top: '12px',
        pointerEvents: 'auto'
      }}>
        {/* ORIGINAL: PNG profile image with SVG fallback for logged in user
        {authUser ? (
          <>
            <img
              src="/images/buttons/profile.png"
              alt="Profile"
              onClick={handleProfileClick}
              style={{
                width: '40px',
                height: '40px',
                cursor: 'pointer',
                display: 'block',
                transition: 'opacity 0.2s'
              }}
              onMouseOver={(e) => e.target.style.opacity = '0.9'}
              onMouseOut={(e) => e.target.style.opacity = '1'}
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }}
            />
            <div
              onClick={handleProfileClick}
              style={{
                display: 'none',
                width: '40px',
                height: '40px',
                background: '#F9A61B',
                borderRadius: '50%',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                transition: 'opacity 0.2s'
              }}
              onMouseOver={(e) => e.currentTarget.style.opacity = '0.9'}
              onMouseOut={(e) => e.currentTarget.style.opacity = '1'}
            >
              <svg width="34" height="34" viewBox="0 0 24 24" fill="white">
                <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
              </svg>
            </div>
          </>
        ) : (
          <button
            onClick={goToLogin}
            style={{
              height: '40px',
              padding: '0 20px',
              background: '#F9A61B',
              color: 'white',
              border: 'none',
              borderRadius: '20px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'opacity 0.2s',
              whiteSpace: 'nowrap'
            }}
            onMouseOver={(e) => e.currentTarget.style.opacity = '0.9'}
            onMouseOut={(e) => e.currentTarget.style.opacity = '1'}
          >
            {t('auth:log_in')}
          </button>
        )}
        */}
        {/* ORIGINAL: inline profile/login buttons — replaced with StandardButton identity variant */}
        <StandardButton
          variant="identity"
          iconName={signedIn ? 'User' : 'LogIn'}
          profileImage={authUser?.avatar_url || null}
          onClick={signedIn ? handleProfileClick : goToLogin}
          title={signedIn ? '' : t('auth:log_in')}
        />
      </div>

      {/* ProfileMenu - Top right quadrant */}
      <ProfileMenu
        isOpen={showProfileMenu}
        onClose={() => setShowProfileMenu(false)}
        currentUser={authUser || currentUser}
      />
      <NotificationsDropdown
        isOpen={showNotifications}
        onClose={() => setShowNotifications(false)}
        currentUser={authUser}
      />

      {/* LocalitySelector Menu - Drops down from arrow */}
      {isLocalityContext && showLocalitySelector && (
        <div
          style={{
            position: 'fixed',
            top: '3px', // Positioned so "My Locations" pill centers on header text (3px + 7px padding = 10px, center at 30px)
            left: 'calc(50% + 180px)',
            transform: 'translateX(-50%)',
            zIndex: 20000,
            background: 'transparent' // Fully transparent background
          }}
        >
          <LocalitySelector
            areas={userAreas}
            onSelectArea={handleSelectArea}
            onChangeLocation={handleChangeLocationClick}
            onManageLocations={handleManageLocationsClick}
            isGuest={!signedIn}
          />
        </div>
      )}

      {/* Map location picker modal (guest browse / logged-in visit) */}
      {showQuickPicker && (
        <MapLocationPicker
          mode="browse"
          onSelect={handleQuickPickerSelect}
          onClose={() => setShowQuickPicker(false)}
          // 2026-09-02 country-selection foundation build: routed through the shared
          // IP-cache reader + default-country chain, replacing the inline localStorage-
          // parsing IIFEs (docs/reuse-backlog.md 2026-09-02 entry).
          detectedBoundaryId={getCachedIpLocation()?.boundaryId || null}
          countryCode={resolveDefaultCountry({ ipCountryCode: getCachedIpLocation()?.countryCode })}
        />
      )}

      {/* Click outside to close locality selector */}
      {showLocalitySelector && (
        <div
          onClick={() => {
            setShowLocalitySelector(false);
            setShowLocalityButton(false);
          }}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 19999,
            pointerEvents: 'auto'
          }}
        />
      )}


        {/* Issues Dropdown - Admin view of user-reported issues */}
        <IssuesDropdown
          isOpen={showIssuesDropdown}
          onClose={() => setShowIssuesDropdown(false)}
          issueCount={issueCount}
          onIssueCountChange={setIssueCount}
        />
      </div>
    </div>
  );
}

export default HeaderButtonsFeed;